import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

/**
 * Reading Progress Context
 * 
 * Tracks:
 * - Time spent reading (per session and total)
 * - Current reading position
 * - Reading streaks
 * - Quran completion progress
 */

const ReadingProgressContext = createContext();

// Storage key
const STORAGE_KEY = 'quranplus_reading_progress';

// Default progress state
const defaultProgress = {
  totalTimeSpent: 0, // Total seconds spent reading
  todayTimeSpent: 0, // Seconds spent today
  lastReadDate: null, // ISO date string
  lastReadSurah: null,
  lastReadAyah: null,
  currentStreak: 0, // Days in a row
  longestStreak: 0,
  sessionsCount: 0,
  surahsCompleted: [], // Array of completed surah numbers
  dailyReadingLog: {}, // { '2024-03-23': { timeSpent: 120, surahsRead: [1, 2] } }
  khatmProgress: null, // 40-day khatm tracker
};

export const ReadingProgressProvider = ({ children }) => {
  const [progress, setProgress] = useState(defaultProgress);
  const [isReading, setIsReading] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const timerRef = useRef(null);

  // Load progress from localStorage
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setProgress({ ...defaultProgress, ...parsed });
      }
    } catch (e) {
      console.error('Failed to load reading progress:', e);
    }
  }, []);

  // Save progress to localStorage
  const saveProgress = useCallback((newProgress) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newProgress));
    } catch (e) {
      console.error('Failed to save reading progress:', e);
    }
  }, []);

  // Get today's date string
  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  // Check and update streak
  const updateStreak = useCallback((currentProgress) => {
    const today = getTodayDate();
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    
    let newStreak = currentProgress.currentStreak;
    
    if (currentProgress.lastReadDate === yesterday) {
      // Continue streak
      newStreak = currentProgress.currentStreak + 1;
    } else if (currentProgress.lastReadDate !== today) {
      // Streak broken, start new
      newStreak = 1;
    }
    
    return {
      currentStreak: newStreak,
      longestStreak: Math.max(newStreak, currentProgress.longestStreak),
    };
  }, []);

  // Start reading session
  const startReading = useCallback((surahNumber, ayahNumber) => {
    if (isReading) return;
    
    setIsReading(true);
    setSessionStartTime(Date.now());
    
    // Update last read position
    setProgress(prev => {
      const updated = {
        ...prev,
        lastReadSurah: surahNumber,
        lastReadAyah: ayahNumber,
      };
      saveProgress(updated);
      return updated;
    });

    // Start timer to track time
    timerRef.current = setInterval(() => {
      setProgress(prev => {
        const today = getTodayDate();
        const dailyLog = { ...prev.dailyReadingLog };
        
        if (!dailyLog[today]) {
          dailyLog[today] = { timeSpent: 0, surahsRead: [] };
        }
        dailyLog[today].timeSpent += 1;
        
        return {
          ...prev,
          totalTimeSpent: prev.totalTimeSpent + 1,
          todayTimeSpent: dailyLog[today].timeSpent,
          dailyReadingLog: dailyLog,
        };
      });
    }, 1000);
  }, [isReading, saveProgress]);

  // Stop reading session
  const stopReading = useCallback(() => {
    if (!isReading) return;
    
    setIsReading(false);
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    const sessionDuration = sessionStartTime ? Math.floor((Date.now() - sessionStartTime) / 1000) : 0;
    setSessionStartTime(null);

    // Update progress with session end
    setProgress(prev => {
      const today = getTodayDate();
      const streakUpdate = updateStreak(prev);
      
      const updated = {
        ...prev,
        ...streakUpdate,
        lastReadDate: today,
        sessionsCount: prev.sessionsCount + 1,
      };
      
      saveProgress(updated);
      return updated;
    });

    return sessionDuration;
  }, [isReading, sessionStartTime, saveProgress, updateStreak]);

  // Mark surah as completed
  const markSurahComplete = useCallback((surahNumber) => {
    setProgress(prev => {
      const today = getTodayDate();
      const surahsCompleted = prev.surahsCompleted.includes(surahNumber)
        ? prev.surahsCompleted
        : [...prev.surahsCompleted, surahNumber];
      
      const dailyLog = { ...prev.dailyReadingLog };
      if (!dailyLog[today]) {
        dailyLog[today] = { timeSpent: 0, surahsRead: [] };
      }
      if (!dailyLog[today].surahsRead.includes(surahNumber)) {
        dailyLog[today].surahsRead.push(surahNumber);
      }
      
      const updated = {
        ...prev,
        surahsCompleted,
        dailyReadingLog: dailyLog,
      };
      
      saveProgress(updated);
      return updated;
    });
  }, [saveProgress]);

  // Update reading position
  const updatePosition = useCallback((surahNumber, ayahNumber) => {
    setProgress(prev => {
      const updated = {
        ...prev,
        lastReadSurah: surahNumber,
        lastReadAyah: ayahNumber,
      };
      saveProgress(updated);
      return updated;
    });
  }, [saveProgress]);

  // Get formatted time string
  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  };

  // Get completion percentage
  const getCompletionPercentage = () => {
    return Math.round((progress.surahsCompleted.length / 114) * 100);
  };

  // Get today's reading time
  const getTodayTime = () => {
    const today = getTodayDate();
    return progress.dailyReadingLog[today]?.timeSpent || 0;
  };

  // Get weekly stats
  const getWeeklyStats = () => {
    const stats = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(Date.now() - i * 86400000).toISOString().split('T')[0];
      const dayLog = progress.dailyReadingLog[date];
      stats.push({
        date,
        day: new Date(date).toLocaleDateString('en', { weekday: 'short' }),
        timeSpent: dayLog?.timeSpent || 0,
        surahsRead: dayLog?.surahsRead?.length || 0,
      });
    }
    return stats;
  };

  // 40-Day Khatm functions
  const startKhatm = useCallback(() => {
    const startDate = getTodayDate();
    const khatmProgress = {
      startDate,
      targetDays: 40,
      dailyTarget: Math.ceil(114 / 40), // ~3 surahs per day
      completedDays: {},
      isActive: true,
    };
    
    setProgress(prev => {
      const updated = { ...prev, khatmProgress };
      saveProgress(updated);
      return updated;
    });
    
    return khatmProgress;
  }, [saveProgress]);

  const updateKhatmDay = useCallback((dayNumber, surahsCompleted) => {
    setProgress(prev => {
      if (!prev.khatmProgress) return prev;
      
      const completedDays = { ...prev.khatmProgress.completedDays };
      completedDays[dayNumber] = {
        date: getTodayDate(),
        surahsCompleted,
        completed: true,
      };
      
      const updated = {
        ...prev,
        khatmProgress: {
          ...prev.khatmProgress,
          completedDays,
        },
      };
      
      saveProgress(updated);
      return updated;
    });
  }, [saveProgress]);

  const resetKhatm = useCallback(() => {
    setProgress(prev => {
      const updated = { ...prev, khatmProgress: null };
      saveProgress(updated);
      return updated;
    });
  }, [saveProgress]);

  // Reset all progress
  const resetProgress = useCallback(() => {
    setProgress(defaultProgress);
    saveProgress(defaultProgress);
  }, [saveProgress]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  return (
    <ReadingProgressContext.Provider value={{
      progress,
      isReading,
      startReading,
      stopReading,
      markSurahComplete,
      updatePosition,
      formatTime,
      getCompletionPercentage,
      getTodayTime,
      getWeeklyStats,
      // Khatm functions
      startKhatm,
      updateKhatmDay,
      resetKhatm,
      // Utils
      resetProgress,
    }}>
      {children}
    </ReadingProgressContext.Provider>
  );
};

export const useReadingProgress = () => {
  const context = useContext(ReadingProgressContext);
  if (!context) {
    throw new Error('useReadingProgress must be used within ReadingProgressProvider');
  }
  return context;
};

export default ReadingProgressContext;
